import { useState, useEffect, useRef } from 'react';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import type { Selection } from '@react-types/shared';
import { Button, useDisclosure } from '@heroui/react';
import {
  Certificate,
  useGetCertificatesByCourseQuery,
  useGetCertificatesCourseFiltersQuery,
  useDeleteCertificatesBatchMutation,
  useDownloadCertificatesBatchMutation,
} from '@/entities/Certificates';
import { Container } from '@/shared/ui/Container';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import { CustomInput, CustomSelect, SelectSort } from '@/shared/ui/Fields';
import { ContainerTable } from '@/shared/ui/AppTable';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import { MessageModal } from '@/shared/ui/Modal';
import { SectionLoader } from '@/shared/ui/Loader';
import { routerPath } from '@/shared/config/router';
import SearchIcon from '@/shared/assets/icons/search.svg';
import { certificatesCourseColumns } from '../../config/certificatesCourseColumns';
import { certificatesCourseSortOptions } from '../../config/certificatesCourseSortOptions';
import { CertificatesSelectedActions } from '../components/CertificatesSelectedActions';
import { GenerateCertificateModal } from '../components/GenerateCertificateModal';
import { CertificateImageModal } from '../components/CertificateImageModal';
import styles from './CertificatesCourseView.module.scss';

interface CertificatesCourseViewProps {
  courseId: number;
}

export const CertificatesCourseView = ({ courseId }: CertificatesCourseViewProps) => {
  const { canEntity } = useAdminAccess();
  const canIssueCertificate = canEntity('certificate', 'create');
  const canDeleteCertificate = canEntity('certificate', 'delete');
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onOpenChange: onDeleteModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isGenerateModalOpen,
    onOpen: onGenerateModalOpen,
    onOpenChange: onGenerateModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isImageModalOpen,
    onOpen: onImageModalOpen,
    onOpenChange: onImageModalOpenChange,
  } = useDisclosure();

  const [certificatePreview, setCertificatePreview] = useState<Certificate | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 8,
  });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 500);
  const [sort, setSort] = useState<string>('issuedAt:desc');
  const [intakeId, setIntakeId] = useState<string | null>(null);
  const [groupNumber, setGroupNumber] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Certificate[]>([]);
  const prevSearchRef = useRef(debouncedSearch);
  const prevSortRef = useRef(sort);
  const prevFiltersRef = useRef({ intakeId, groupNumber });

  const { toast } = useToast();
  const [deleteCertificatesBatch, { isLoading: isDeletingCertificates }] =
    useDeleteCertificatesBatchMutation();
  const [downloadCertificatesBatch, { isLoading: isDownloadingCertificates }] =
    useDownloadCertificatesBatchMutation();
  const { data: filtersData } = useGetCertificatesCourseFiltersQuery(courseId);
  const intakes = filtersData?.data?.intakes ?? [];
  const selectedIntake = intakes.find((i) => String(i.id) === intakeId);
  const groupNumbers = selectedIntake?.groupNumbers ?? [];

  const {
    data,
    isLoading,
    refetch: refetchCertificates,
  } = useGetCertificatesByCourseQuery({
    courseId,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    search: debouncedSearch || undefined,
    sort: sort === 'default' ? undefined : sort,
    intakeId: intakeId ? Number(intakeId) : undefined,
    groupNumber: groupNumber ? Number(groupNumber) : undefined,
  });

  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch || prevSortRef.current !== sort) {
      prevSearchRef.current = debouncedSearch;
      prevSortRef.current = sort;
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [debouncedSearch, sort]);

  useEffect(() => {
    if (
      prevFiltersRef.current.intakeId !== intakeId ||
      prevFiltersRef.current.groupNumber !== groupNumber
    ) {
      prevFiltersRef.current = { intakeId, groupNumber };
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [intakeId, groupNumber]);

  useEffect(() => {
    if (intakeId && !intakes.some((i) => String(i.id) === intakeId)) {
      setIntakeId(null);
      setGroupNumber(null);
    }
  }, [intakeId, intakes]);

  useEffect(() => {
    if (groupNumber && !groupNumbers.includes(Number(groupNumber))) {
      setGroupNumber(null);
    }
  }, [groupNumber, groupNumbers]);

  const certificates = data?.data?.certificates ?? [];
  const paginationMeta = data?.data?.pagination;
  const course = data?.data?.course ?? filtersData?.data?.course;
  const total = paginationMeta?.total ?? 0;

  const handleSortChange = (keys: Selection) => {
    const newValue = new Set(keys).values().next().value ?? 'default';
    setSort(newValue as string);
  };

  const handleSaveSelected = async () => {
    if (selectedRows.length === 0) return;
    try {
      const blob = await downloadCertificatesBatch({
        ids: selectedRows.map((c) => c.id),
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'certificates.zip';
      link.click();
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Success',
        description: 'Certificates downloaded successfully',
        color: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message ?? 'Failed to download certificates',
        color: 'danger',
      });
    }
  };

  const handleDeleteSelected = () => {
    onDeleteModalOpen();
  };

  const handleConfirmDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      onDeleteModalOpenChange();
      return;
    }
    try {
      await deleteCertificatesBatch({
        ids: selectedRows.map((c) => c.id),
      }).unwrap();
      toast({
        title: 'Success',
        description: `${selectedRows.length} certificate(s) deleted successfully`,
        color: 'success',
      });
      setSelectedRows([]);
      refetchCertificates();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message ?? 'Failed to delete certificates',
        color: 'danger',
      });
    }
    onDeleteModalOpenChange();
  };

  const handleGenerateCertificate = () => {
    onGenerateModalOpen();
  };

  const handleGenerateModalSuccess = () => {
    refetchCertificates();
  };

  const handleRowClick = (certificate: Certificate) => {
    setCertificatePreview(certificate);
    onImageModalOpen();
  };

  const handleImageModalClose = (open: boolean) => {
    if (!open) setCertificatePreview(null);
    onImageModalOpenChange();
  };

  const tableMeta = paginationMeta
    ? {
        page: paginationMeta.page,
        pageSize: paginationMeta.pageSize,
        total: paginationMeta.total,
        pageCount: paginationMeta.pageCount,
      }
    : undefined;

  if (isLoading && !data) {
    return <SectionLoader />;
  }

  const courseTitle = course?.title ?? 'Course';

  return (
    <div className={styles.root}>
      <Container className="flex flex-col gap-4">
        <CourseBreadcrumbs
          items={[{ label: 'Certificates', href: routerPath.certificates }, { label: courseTitle }]}
        />

        <div className="inline-flex gap-x-2 items-end">
          <h2 className="text-[32px] font-medium">{courseTitle}</h2>
          <p className="text-grey-secondary leading-[220%]">{total}</p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="max-w-[530px] w-full">
              <CustomInput
                className={searchInputClassName}
                startContent={<Svg Icon={SearchIcon} />}
                placeholder="Search"
                value={search}
                onValueChange={setSearch}
              />
            </div>
            {canIssueCertificate && (
              <Button
                variant="light"
                className="text-white bg-[#211D55]  hover:!bg-black"
                onPress={handleGenerateCertificate}
                isDisabled={!intakeId}
              >
                Generate a certificate
              </Button>
            )}
            <div className="flex items-center gap-4 w-full">
              <div className="w-full">
                <CustomSelect
                  fullWidth
                  style="black"
                  label="Intake"
                  labelPlacement="outside"
                  placeholder="Intake"
                  items={intakes.map((i) => ({ key: String(i.id), label: i.title }))}
                  selectedKeys={intakeId ? new Set([intakeId]) : new Set()}
                  onSelectionChange={(keys) => {
                    const v = Array.from(keys)[0] as string | undefined;
                    setIntakeId(v ?? null);
                    setGroupNumber(null);
                  }}
                />
              </div>
              <div className="w-full">
                <CustomSelect
                  fullWidth
                  style="black"
                  label="Group"
                  labelPlacement="outside"
                  placeholder={intakeId ? 'Group' : 'Select intake first'}
                  items={groupNumbers.map((n) => ({ key: String(n), label: `Group ${n}` }))}
                  selectedKeys={groupNumber ? new Set([groupNumber]) : new Set()}
                  onSelectionChange={(keys) => {
                    const v = Array.from(keys)[0] as string | undefined;
                    setGroupNumber(v ?? null);
                  }}
                  isDisabled={!intakeId}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <CertificatesSelectedActions
                selectedRows={selectedRows}
                onSaveSelected={handleSaveSelected}
                onDeleteSelected={canDeleteCertificate ? handleDeleteSelected : undefined}
                isDeleting={isDeletingCertificates}
                isDownloading={isDownloadingCertificates}
              />
            </div>
            <SelectSort
              sections={certificatesCourseSortOptions}
              defaultValue={sort === 'issuedAt:desc' ? 'default' : sort}
              onSelectionChange={handleSortChange}
            />
          </div>

          <ContainerTable<Certificate>
            data={certificates}
            meta={tableMeta}
            columns={certificatesCourseColumns}
            pagination={pagination}
            setPagination={setPagination as OnChangeFn<PaginationState>}
            hideTopActions={true}
            onSelectedItemsChange={setSelectedRows}
            onRowClick={handleRowClick}
          />
        </div>
      </Container>

      <MessageModal
        title="Delete Selected Certificates"
        text={`Are you sure you want to delete ${selectedRows.length} selected certificate(s)? This action cannot be undone. Associated PDF files will be removed.`}
        confirmText="Delete"
        isOpen={isDeleteModalOpen}
        onOpenChange={onDeleteModalOpenChange}
        variant="red"
        toggleVisibility={handleConfirmDeleteSelected}
        isLoading={isDeletingCertificates}
      />
      <GenerateCertificateModal
        isOpen={isGenerateModalOpen}
        onOpenChange={onGenerateModalOpenChange}
        courseId={courseId}
        courseTitle={courseTitle}
        intakeId={intakeId ? Number(intakeId) : null}
        groupNumber={groupNumber ? Number(groupNumber) : null}
        onSuccess={handleGenerateModalSuccess}
      />
      <CertificateImageModal
        isOpen={isImageModalOpen}
        onOpenChange={handleImageModalClose}
        certificate={certificatePreview}
      />
    </div>
  );
};
